import React, { useEffect, useState, useRef } from "react";
import moment from "moment-timezone";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { getAuthData } from "../../../../../services/storage/LocalStorage";
import Select from "react-select"
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CssTextField } from "./Styled";
import { isImageReachable } from "../../../../../utils/general";
import { ImagePath } from "../../../../../assets/ImagePath";
import CachedImage from "../../../../common/CachedImage";
import { Mandatory } from "../../FormHelpers";
import { normalizeTimezone } from "../../../../../utils/moment";

dayjs.extend(utc);
dayjs.extend(timezone);

// Custom DateTime Picker Component
const CustomDateTimePicker = ({
  value,
  onChange,
  onAccept,
  disabled = false,
  title = "Select Date & Time",
  mandatory = false,
  iconUrl = null,
  isValidIconUrl = false,
  validation = false,
  validationResult = true,
  validationMessage = "",
  id,
  type = "datetime",
  islabelvisible
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(value ? value.month() : new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(value ? value.year() : new Date().getFullYear());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(value ? value.format('HH:mm') : '17:45');

  const dropdownRef = useRef(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00'
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  useEffect(() => {
    if (value) {
      setSelectedDate(value)
    }
  }, [value])
  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateSelect = (day) => {
    const newDate = dayjs().year(currentYear).month(currentMonth).date(day);
    const [hours, minutes] = selectedTimeSlot.split(':').map(Number);
    const finalDate = newDate.hour(hours).minute(minutes).second(0);

    setSelectedDate(finalDate);
    onChange && onChange(finalDate);
    if (type == 'date') {
      setIsOpen(false);
    }
  };

  const handleTimeSelect = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
    if (selectedDate) {
      const [hours, minutes] = timeSlot.split(':').map(Number);
      const finalDate = selectedDate.hour(hours).minute(minutes).second(0);
      setSelectedDate(finalDate);
      onChange && onChange(finalDate);
      setIsOpen(false);
    }
  };

  const handleAccept = () => {
    if (selectedDate && onAccept) {
      onAccept(selectedDate);
    }
    setIsOpen(false);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const daysInPrevMonth = getDaysInMonth(currentMonth === 0 ? 11 : currentMonth - 1, currentMonth === 0 ? currentYear - 1 : currentYear);

    const days = [];

    // Previous month's trailing days
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      days.push(
        <button
          key={`prev-${day}`}
          className="w-[39px] h-[37px] text-[#475569] hover:bg-[#3F39FD1F] rounded-10 text-size-14"
          onClick={() => {
            handlePrevMonth();
            handleDateSelect(day);
          }}
        >
          {day}
        </button>
      );
    }

    // Current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDate &&
        selectedDate.date() === day &&
        selectedDate.month() === currentMonth &&
        selectedDate.year() === currentYear;

      days.push(
        <button
          key={`current-${day}`}
          onClick={() => handleDateSelect(day)}
          className={`w-[39px] h-[37px] text-size-14 rounded-10 font-medium transition-colors text-black ${isSelected
            ? 'bg-[#638DFF] text-white font-semibold cstom-main-shadow'
            : 'text-black hover:bg-[#3F39FD1F]'
            }`}
        >
          {day}
        </button>
      );
    }

    // Next month's leading days to fill the grid
    const totalCells = Math.ceil(days.length / 7) * 7;
    const remainingCells = totalCells - days.length;

    for (let day = 1; day <= remainingCells; day++) {
      days.push(
        <button
          key={`next-${day}`}
          className="w-[39px] h-[37px] text-[#475569] hover:bg-[#3F39FD1F] rounded-10 text-size-14"
          onClick={() => {
            handleNextMonth();
            handleDateSelect(day);
          }}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const monthOptions = months.map((m, idx) => ({ value: idx, label: m }));
  const yearOptions = Array.from({ length: 200 }, (_, i) => {
    const y = currentYear - 125 + i;
    return { value: y, label: y.toString() };
  });

  // custom style overrides (to mimic your Tailwind look)
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "40px",
      borderRadius: "10px",
      borderColor: "#0000001A",
      boxShadow: "none",
      "&:hover": { borderColor: "#0000001A" },
      fontSize: "14px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#000",
      fontWeight: 400,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "4px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  const formatSelectedDateTime = () => {
    if (!selectedDate) return '';
    const format = type === 'date' ? 'DD/MM/YYYY' : 'DD/MM/YYYY | HH:mm:ss';
    return selectedDate.format(format);
  };

  return (
    <div className="flex items-start gap-1">
      <div className="flex flex-col w-full">
        {/* Label with Icon */}
        <div className="flex items-center gap-1 mb-[5px]">
          {!islabelvisible && <div className="min-w-[17px] min-h-[17px] bg-blue-100 rounded-xl flex items-center justify-center overflow-hidden">
            {iconUrl && isValidIconUrl ? (
              <CachedImage
                image={iconUrl}
                imgKey="date-picker-Icon1"
                className="w-[17px] h-[17px]"
              />
            ) : (
              <CachedImage
                image={ImagePath.dateAndTimePickerField}
                imgKey="date-picker-Icon1"
                className="w-[17px] h-[17px]"
              />
            )}
          </div>}
          {!islabelvisible && <label className="text-size-14 text-[#15194B] font-regular mt-[2px]">
            {title}
            <Mandatory mandatory={mandatory} />
          </label>}
        </div>

        {/* Input Field */}
        <div className="relative" ref={dropdownRef}>
          <input
            value={formatSelectedDateTime()}
            placeholder={type == 'date' ? "DD/MM/YYYY" : "DD/MM/YYYY | HH:MM:SS"}
            className="w-full pl-4 pr-10 py-2 cursor-pointer border border-[#c4d8ffde] rounded-md focus:outline-none bg-blue-50 text-sm text-[#000000] !h-[40px] placeholder:!text-[#44485A80]"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            readOnly
            disabled={disabled}
          />

          {/* Calendar Icon */}
          <div className="absolute right-3 top-[7px] -translate-y-1/2 text-gray-700 cursor-pointer">
            <span className="date-icon bg-white absolute right-[-2px] top-[2px] cursor-pointer pointer-events-none inline-block z-10 w-[20px]">
              <svg width="21" height="21" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer">
                <path d="M18.8477 11.7305C19.387 11.7305 19.8242 11.2932 19.8242 10.7539C19.8242 10.2146 19.387 9.77734 18.8477 9.77734C18.3083 9.77734 17.8711 10.2146 17.8711 10.7539C17.8711 11.2932 18.3083 11.7305 18.8477 11.7305Z" fill="black" />
                <path d="M21.0938 2.45312H19.8242V1.47656C19.8242 0.937207 19.387 0.5 18.8477 0.5C18.3083 0.5 17.8711 0.937207 17.8711 1.47656V2.45312H13.4277V1.47656C13.4277 0.937207 12.9905 0.5 12.4512 0.5C11.9118 0.5 11.4746 0.937207 11.4746 1.47656V2.45312H7.08008V1.47656C7.08008 0.937207 6.64287 0.5 6.10352 0.5C5.56416 0.5 5.12695 0.937207 5.12695 1.47656V2.45312H3.90625C1.75234 2.45312 0 4.20547 0 6.35938V21.5938C0 23.7477 1.75234 25.5 3.90625 25.5H11.377C11.9163 25.5 12.3535 25.0628 12.3535 24.5234C12.3535 23.9841 11.9163 23.5469 11.377 23.5469H3.90625C2.8293 23.5469 1.95312 22.6707 1.95312 21.5938V6.35938C1.95312 5.28242 2.8293 4.40625 3.90625 4.40625H5.12695V5.38281C5.12695 5.92217 5.56416 6.35938 6.10352 6.35938C6.64287 6.35938 7.08008 5.92217 7.08008 5.38281V4.40625H11.4746V5.38281C11.4746 5.92217 11.9118 6.35938 12.4512 6.35938C12.9905 6.35938 13.4277 5.92217 13.4277 5.38281V4.40625H17.8711V5.38281C17.8711 5.92217 18.3083 6.35938 18.8477 6.35938C19.387 6.35938 19.8242 5.92217 19.8242 5.38281V4.40625H21.0938C22.1707 4.40625 23.0469 5.28242 23.0469 6.35938V11.9258C23.0469 12.4651 23.4841 12.9023 24.0234 12.9023C24.5628 12.9023 25 12.4651 25 11.9258V6.35938C25 4.20547 23.2477 2.45312 21.0938 2.45312Z" fill="black" />
                <path d="M19.0918 13.6836C15.834 13.6836 13.1836 16.334 13.1836 19.5918C13.1836 22.8496 15.834 25.5 19.0918 25.5C22.3496 25.5 25 22.8496 25 19.5918C25 16.334 22.3496 13.6836 19.0918 13.6836ZM19.0918 23.5469C16.911 23.5469 15.1367 21.7727 15.1367 19.5918C15.1367 17.4109 16.911 15.6367 19.0918 15.6367C21.2726 15.6367 23.0469 17.4109 23.0469 19.5918C23.0469 21.7727 21.2726 23.5469 19.0918 23.5469Z" fill="black" />
                <path d="M20.5078 18.6152H20.0684V17.5898C20.0684 17.0505 19.6312 16.6133 19.0918 16.6133C18.5524 16.6133 18.1152 17.0505 18.1152 17.5898V19.5918C18.1152 20.1312 18.5524 20.5684 19.0918 20.5684H20.5078C21.0472 20.5684 21.4844 20.1312 21.4844 19.5918C21.4844 19.0524 21.0472 18.6152 20.5078 18.6152Z" fill="black" />
                <path d="M14.5996 11.7305C15.1389 11.7305 15.5762 11.2932 15.5762 10.7539C15.5762 10.2146 15.1389 9.77734 14.5996 9.77734C14.0603 9.77734 13.623 10.2146 13.623 10.7539C13.623 11.2932 14.0603 11.7305 14.5996 11.7305Z" fill="black" />
                <path d="M10.3516 15.9785C10.8909 15.9785 11.3281 15.5413 11.3281 15.002C11.3281 14.4626 10.8909 14.0254 10.3516 14.0254C9.81222 14.0254 9.375 14.4626 9.375 15.002C9.375 15.5413 9.81222 15.9785 10.3516 15.9785Z" fill="black" />
                <path d="M6.10352 11.7305C6.64286 11.7305 7.08008 11.2932 7.08008 10.7539C7.08008 10.2146 6.64286 9.77734 6.10352 9.77734C5.56418 9.77734 5.12695 10.2146 5.12695 10.7539C5.12695 11.2932 5.56418 11.7305 6.10352 11.7305Z" fill="black" />
                <path d="M6.10352 15.9785C6.64286 15.9785 7.08008 15.5413 7.08008 15.002C7.08008 14.4626 6.64286 14.0254 6.10352 14.0254C5.56418 14.0254 5.12695 14.4626 5.12695 15.002C5.12695 15.5413 5.56418 15.9785 6.10352 15.9785Z" fill="black" />
                <path d="M6.10352 20.2266C6.64286 20.2266 7.08008 19.7893 7.08008 19.25C7.08008 18.7107 6.64286 18.2734 6.10352 18.2734C5.56418 18.2734 5.12695 18.7107 5.12695 19.25C5.12695 19.7893 5.56418 20.2266 6.10352 20.2266Z" fill="black" />
                <path d="M10.3516 20.2266C10.8909 20.2266 11.3281 19.7893 11.3281 19.25C11.3281 18.7107 10.8909 18.2734 10.3516 18.2734C9.81222 18.2734 9.375 18.7107 9.375 19.25C9.375 19.7893 9.81222 20.2266 10.3516 20.2266Z" fill="black" />
                <path d="M10.3516 11.7305C10.8909 11.7305 11.3281 11.2932 11.3281 10.7539C11.3281 10.2146 10.8909 9.77734 10.3516 9.77734C9.81222 9.77734 9.375 10.2146 9.375 10.7539C9.375 11.2932 9.81222 11.7305 10.3516 11.7305Z" fill="black" />
              </svg>
            </span>
          </div>

          {/* Custom Dropdown */}
          {isOpen && (
            <div className="absolute z-50 mt-2 bg-white rounded-[10px] shadow-xl border border-[#0000001A] text-black w-[325px]">
              <div className="p-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-4 gap-2">
                  <button
                    onClick={handlePrevMonth}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0.223772 6.12857C0.223772 5.9298 0.294456 5.75778 0.435826 5.61253L5.43583 0.475028C5.57719 0.329771 5.74461 0.257143 5.93806 0.257143C6.13151 0.257143 6.29892 0.329771 6.44029 0.475028C6.58166 0.620285 6.65234 0.792299 6.65234 0.991072L6.65234 11.2661C6.65234 11.4648 6.58166 11.6369 6.44029 11.7821C6.29892 11.9274 6.13151 12 5.93806 12C5.74461 12 5.57719 11.9274 5.43583 11.7821L0.435826 6.64462C0.294456 6.49936 0.223772 6.32734 0.223772 6.12857Z" fill="#A3A3A3" />
                    </svg>
                  </button>

                  <div className="flex space-x-2 w-full">
                    <div className="relative w-full">
                      <Select
                        value={monthOptions.find(opt => opt.value === currentMonth)}
                        onChange={(opt) => setCurrentMonth(opt.value)}
                        options={monthOptions}
                        isSearchable={false}
                        styles={selectStyles}
                      />

                    </div>
                    <div className="relative w-full">
                      <Select
                        value={yearOptions.find(opt => opt.value === currentYear)}
                        onChange={(opt) => setCurrentYear(opt.value)}
                        options={yearOptions}
                        isSearchable={false}
                        styles={selectStyles}
                      />

                    </div>
                  </div>

                  <button
                    onClick={handleNextMonth}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.65123 5.87143C6.65123 6.0702 6.58054 6.24222 6.43917 6.38747L1.43917 11.525C1.29781 11.6702 1.13039 11.7429 0.936942 11.7429C0.74349 11.7429 0.576079 11.6702 0.43471 11.525C0.293341 11.3797 0.222656 11.2077 0.222656 11.0089V0.733929C0.222656 0.535156 0.293341 0.363142 0.43471 0.217885C0.576079 0.0726283 0.74349 0 0.936942 0C1.13039 0 1.29781 0.0726283 1.43917 0.217885L6.43917 5.35538C6.58054 5.50064 6.65123 5.67266 6.65123 5.87143Z" fill="#A3A3A3" />
                    </svg>
                  </button>
                </div>

                <div className="flex">
                  {/* Calendar */}
                  <div className="flex-1">
                    {/* Weekday headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {weekdays.map(day => (
                        <div key={day} className="text-center text-xs font-medium py-2">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar days */}
                    <div className="grid grid-cols-7 gap-1">
                      {renderCalendarDays()}
                    </div>
                  </div>

                  {/* Time slots */}
                  {type !== "date" && (
                    <div className="ml-6 w-20">
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {timeSlots.map(time => {
                          const isSelected = selectedTimeSlot === time;

                          return (
                            <button
                              key={time}
                              onClick={() => handleTimeSelect(time)}
                              className={`w-full px-2 py-1 text-size-14 rounded-10 transition-colors ${isSelected
                                ? 'bg-[#638DFF] text-white font-semibold cstom-main-shadow'
                                : 'text-black hover:bg-[#3F39FD1F]'
                                }`}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Selected date display */}
                <div className=" pt-4">
                  <div className="flex items-center gap-2 border border-1 border-[#D3D9E8] rounded-[5px] h-[38px]">
                    <span className="text-black bg-[#E9EDF3] h-[38px] px-2 flex items-center">Start Date</span>
                    <span className="font-medium text-black">
                      {selectedDate
                        ? `${months[selectedDate.month()]} ${selectedDate.date()}, ${selectedDate.year()} ${type !== 'date' ? `@ ${selectedTimeSlot}:00` : ''}`
                        : 'No date selected'}
                    </span>

                  </div>
                </div>

                {/* Action buttons */}
                {/* <div className="flex justify-end mt-4 space-x-2">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAccept}
                    className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800"
                  >
                    OK
                  </button>
                </div> */}
              </div>
            </div>
          )}
        </div>

        {/* Validation Message */}
        {!validation && validationResult === false && validationMessage && (
          <p className="mt-1 text-[14px] text-red">{validationMessage}</p>
        )}
      </div>
    </div>
  );
};

const DateTimeInput = (props) => {
  const {
    id,
    info,
    readOnly,
    type,
    value,
    handleChangeDateTime,
    handleBlurDateTime,
    handleBlurTime,
    handleChangeTime,
    handleBlurDate,
    handleChangeData,
    mandatory,
    disableFormSubmit,
    title,
    islabelvisible,
    iconUrl = null,
  } = props;
  const [isValidIconUrl, setIsValidIconUrl] = useState(false);
  const [validDate, setValidDate] = useState(true);

  let { validation, validationMessage, validationResult } = props;
  const isBoolean = (val) => "boolean" === typeof val;
  const { user: { userTimezone } } = getAuthData();

  const defaultUserTimezone = "Etc/UTC";
  const normalizedTz = normalizeTimezone(userTimezone || defaultUserTimezone);
  moment.tz.setDefault(userTimezone || defaultUserTimezone);

  useEffect(() => {
    const checkImage = async () => {
      if (iconUrl) {
        const result = await isImageReachable(iconUrl);
        setIsValidIconUrl(result);
      }
    };

    checkImage();
  }, [iconUrl]);

  const convertDateTimeFormat = (dateTimeInput, type) => {
    let formatedDate;

    if (dateTimeInput) {
      const convertDate = (dateInput) => {
        let splittedParts = dateInput?.split("-");
        let convertedDate =
          splittedParts[2] + "-" + splittedParts[1] + "-" + splittedParts[0];
        return convertedDate;
      };
      let dateInput;

      if (type === "date") {
        formatedDate = convertDate(dateTimeInput);
      } else if (type === "datetime") {
        dateInput = dateTimeInput?.slice(0, 10);
        let timeInput = dateTimeInput?.slice(10, 16);
        formatedDate = convertDate(dateInput) + timeInput;
      } else {
        formatedDate = dateInput;
      }
      return formatedDate;
    }
    return dateTimeInput;
  };

  switch (type) {
    case "date":
      return (
        <CustomDateTimePicker
          value={value ? dayjs(value).tz(normalizedTz) : null}
          inputFormat="DD-MM-YYYY"
          label={!islabelvisible ? title : null}
          disabled={readOnly}
          onAccept={(newValue) => {
            if (newValue) {
              const formattedValue = newValue.format("YYYY-MM-DD");
              handleBlurDate(formattedValue, id)
            }
          }}
          onChange={(newValue) => {
            if (!validDate) setValidDate(true);
            const dateValue = newValue && newValue.isValid();
            if (dateValue) {
              const formattedValue = newValue.format("YYYY-MM-DD");
              handleChangeData(formattedValue, id)
            } else {
              if (!mandatory) {
                handleChangeData(null, id);
              }
              setValidDate(false);
            }
          }}
          title={title}
          type={type}
          mandatory={mandatory}
          iconUrl={iconUrl}
          isValidIconUrl={isValidIconUrl}
          validation={validation}
          validationResult={validationResult}
          validationMessage={validationMessage}
          id={id}
          islabelvisible={islabelvisible}
        />
      );

    case "time":
      return (
        <TimePicker
          inputFormat="HH:mm"
          label={!islabelvisible ? title : null}
          disabled={readOnly}
          value={value ? dayjs(value).tz(normalizedTz) : null}
          onAccept={(newValue) => {
            if (newValue) {
              const formattedValue = newValue?.format("HH:mm");
              handleBlurTime(formattedValue, id);
            }
          }}
          onChange={(newValue) => {
            const formattedValue = newValue?.format("HH:mm");
            handleChangeTime(formattedValue, id);
          }}
          islabelvisible={islabelvisible}
          renderInput={(params) => (
            <CssTextField
              variant="outlined"
              required={mandatory}
              id={id}
              name={id}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              helperText={
                !props.inline &&
                (isBoolean(validationResult) && !validationResult
                  ? validationMessage
                  : info)
              }
              error={
                validation && isBoolean(validationResult)
                  ? !validationResult
                  : false
              }
              onBlur={(e) => {
                if (e.target.value) handleBlurTime(e.target.value, id);
              }}
              {...params}
            />
          )}
        />
      );

    case "datetime":
      return (
        <CustomDateTimePicker
          value={value ? dayjs(value).tz(normalizedTz) : null}
          onChange={(newValue) => {
            if (!validDate) setValidDate(true);
            const dateValue = new Date(newValue).toString() === "Invalid Date";
            if (dateValue) {
              return;
            }
            const formattedValue = newValue?.format("YYYY-MM-DD HH:mm");
            handleChangeDateTime(formattedValue, id);
          }}
          onAccept={(newValue) => {
            if (newValue) {
              const formattedValue = newValue?.format("YYYY-MM-DD HH:mm");
              handleBlurDateTime(formattedValue, id);
            }
          }}
          disabled={readOnly}
          title={title}
          type={type}
          mandatory={mandatory}
          iconUrl={iconUrl}
          isValidIconUrl={isValidIconUrl}
          validation={validation}
          validationResult={validationResult}
          validationMessage={validationMessage}
          islabelvisible={islabelvisible}
          id={id}
        />
      );
  }
};

export default DateTimeInput;